/**
 * App Submission Page — 앱 공유 신청 / 신청 내용 보기
 *
 * 내 앱에서 "신청 내용 보기" / "수정 후 재신청" / "이어서 작성" 클릭 시 이동
 * mode: 'view' | 'edit'
 */

import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { Button, useToast } from '../components/ui';
import './AppSubmissionPage.css';

// ── 제출 목업 데이터 (내 앱 목록과 연동) ──────────────────────────────────────

type SubmissionStatus = 'review' | 'change' | 'live' | 'reject' | 'draft';

interface Submission {
  id: string;
  appName: string;
  shortDesc: string;
  fileName: string;
  fileSize: string;
  bot: string;
  chatDate: string;
  category: string;
  kind: 'HTML 문서' | '웹앱';
  howto: string;
  scope: string;
  status: SubmissionStatus;
  reviewer?: string;
  comment?: string;
}

// ── 산출물 HTML 미리보기 (목업 렌더) ─────────────────────────────────────────

function PreviewContent({ submission: sub }: { submission: Submission }) {
  // AWS 비용 관련
  if (sub.id === 'm1') {
    return (
      <div className="asub-pc">
        <div className="asub-pc-header"><strong>AWS 비용 이상징후 알림 카드</strong><span>기준: 2026-10-23</span></div>
        <div className="asub-pc-cards">
          {[
            { label: '이상 감지', value: '4건', sub: '전일 대비 +50% 초과', color: '#FDECEA', text: '#A8231A' },
            { label: '최대 급등', value: '+182%', sub: '고객사 A · EC2', color: '#FDF1DC', text: '#8A4B00' },
            { label: '총 초과 비용', value: '$3,240', sub: '어제 대비 추가', color: '#EEECFC', text: '#3A31A8' },
          ].map(c => (
            <div key={c.label} className="asub-pc-kpi" style={{ background: c.color }}>
              <span style={{ color: c.text, fontSize: 12 }}>{c.label}</span>
              <strong style={{ fontSize: 24, color: c.text }}>{c.value}</strong>
              <span style={{ fontSize: 12, color: c.text, opacity: 0.8 }}>{c.sub}</span>
            </div>
          ))}
        </div>
        <table className="asub-pc-table">
          <thead><tr><th>고객사</th><th>서비스</th><th>증가율</th><th>원인 후보</th></tr></thead>
          <tbody>
            <tr><td>고객사 A</td><td>EC2</td><td style={{ color: '#A8231A', fontWeight: 700 }}>+182%</td><td>예약 인스턴스 만료로 온디맨드 전환</td></tr>
            <tr><td>고객사 B</td><td>RDS</td><td style={{ color: '#A8231A', fontWeight: 700 }}>+74%</td><td>스냅샷 백업 주기 변경</td></tr>
            <tr><td>고객사 C</td><td>S3</td><td style={{ color: '#8A4B00' }}>+51%</td><td>대용량 파일 업로드 감지</td></tr>
          </tbody>
        </table>
      </div>
    );
  }

  // 파트너 매출 집계표
  if (sub.id === 'm2') {
    return (
      <div className="asub-pc">
        <div className="asub-pc-header"><strong>파트너 매출(PRM) 집계표</strong><span>2026년 10월</span></div>
        <div className="asub-pc-cards">
          {[
            { label: '총 파트너 매출', value: '₩128M', sub: '전월 대비 +9.2%', color: '#E6F4EC', text: '#1E6B44' },
            { label: '파트너 수',      value: '14곳',   sub: '신규 1곳',         color: '#EEECFC', text: '#3A31A8' },
            { label: '목표 달성률',    value: '84%',    sub: '목표 ₩152M',       color: '#FDF1DC', text: '#8A4B00' },
          ].map(c => (
            <div key={c.label} className="asub-pc-kpi" style={{ background: c.color }}>
              <span style={{ color: c.text, fontSize: 12 }}>{c.label}</span>
              <strong style={{ fontSize: 24, color: c.text }}>{c.value}</strong>
              <span style={{ fontSize: 12, color: c.text, opacity: 0.8 }}>{c.sub}</span>
            </div>
          ))}
        </div>
        <table className="asub-pc-table">
          <thead><tr><th>파트너사</th><th>10월 매출</th><th>전월 대비</th><th>목표 달성</th></tr></thead>
          <tbody>
            <tr><td>파트너 A</td><td>₩32M</td><td style={{ color: '#1E6B44' }}>+15%</td><td>✅ 달성</td></tr>
            <tr><td>파트너 B</td><td>₩28M</td><td style={{ color: '#1E6B44' }}>+7%</td><td>✅ 달성</td></tr>
            <tr><td>파트너 C</td><td>₩18M</td><td style={{ color: '#A8231A' }}>-3%</td><td>⚠ 미달</td></tr>
          </tbody>
        </table>
        <div className="asub-pc-warn">
          ⚠ 파트너사 실명이 코드에 하드코딩됨 — 수정 요청 사항
        </div>
      </div>
    );
  }

  // 기본: 간단한 문서 미리보기
  return (
    <div className="asub-pc">
      <div className="asub-pc-header"><strong>{sub.appName}</strong></div>
      <p style={{ color: '#5F6478', fontSize: 14, margin: 0 }}>{sub.shortDesc}</p>
      <div className="asub-pc-doc-skeleton">
        {[100, 92, 85, 78, 100, 70, 88, 60].map((w, i) => (
          <span key={i} style={{ width: `${w}%`, height: 8, borderRadius: 4, background: i === 0 ? '#9AA0B4' : '#E4E6EF', display: 'block' }} />
        ))}
      </div>
    </div>
  );
}

// ── 목업 데이터 ────────────────────────────────────────────────────────────────

const SUBMISSIONS: Record<string, Submission> = {  m1: {
    id: 'm1',
    appName: 'AWS 비용 이상징후 알림 카드',
    shortDesc: '전일 대비 비용이 급증한 고객사·서비스를 카드로 보여주고 원인 후보를 요약합니다.',
    fileName: 'aws_cost_anomaly_card.html',
    fileSize: '36KB',
    bot: '워킹온 DB 커넥터',
    chatDate: '10.23(금) 14:02',
    category: '데이터·리포트',
    kind: 'HTML 문서',
    howto: '고객사를 선택하지 않으면 전체 고객사 기준으로 표시됩니다. 임계값은 상단 설정에서 바꿀 수 있어요.',
    scope: '에티버스 테넌트 전체',
    status: 'review',
  },
  m2: {
    id: 'm2',
    appName: '파트너 매출(PRM) 집계표',
    shortDesc: '파트너사별 월간 매출을 집계해 테이블과 차트로 보여줍니다.',
    fileName: 'partner_revenue_summary.html',
    fileSize: '42KB',
    bot: '워킹온 DB 커넥터',
    chatDate: '10.22(목) 10:15',
    category: '데이터·리포트',
    kind: '웹앱',
    howto: '월을 선택하면 해당 월의 파트너별 매출 현황이 표시됩니다.',
    scope: '이테크 시스템',
    status: 'change',
    reviewer: '엄재한 · 고객사 관리자 · 10.22',
    comment: '고객사별 매출 금액이 화면에 그대로 들어 있어요. 실행하는 사람 권한으로 워킹온 DB에서 조회하도록 데이터 연결을 설정해 주세요.',
  },
  m3: {
    id: 'm3',
    appName: 'EC2 Right-sizing 계산기',
    shortDesc: '인스턴스 사용률을 입력하면 권장 타입과 월 절감 예상액을 계산합니다.',
    fileName: 'ec2_rightsizing.html',
    fileSize: '28KB',
    bot: '웹검색',
    chatDate: '10.17(목) 09:30',
    category: '클라우드',
    kind: '웹앱',
    howto: '현재 인스턴스 타입과 평균 CPU·메모리 사용률을 입력하세요.',
    scope: '에티버스 테넌트 전체',
    status: 'live',
  },
  m5: {
    id: 'm5',
    appName: '사내 점심 메뉴 추천기',
    shortDesc: '현재 위치 근처 음식점을 추천해드립니다.',
    fileName: 'lunch_recommender.html',
    fileSize: '18KB',
    bot: '기본 채팅',
    chatDate: '10.15(화) 12:00',
    category: '업무생산성',
    kind: '웹앱',
    howto: '주소를 입력하면 주변 음식점을 추천합니다.',
    scope: '에티버스 테넌트 전체',
    status: 'reject',
    reviewer: '어드민 · 테넌트 관리자 · 10.16',
    comment: '업무 관련성이 낮아 테넌트 전체 공개는 어렵습니다. 팀원 몇 명에게만 공유하는 건 괜찮아요.',
  },
  m6: {
    id: 'm6',
    appName: 'HMM 제안 일정표',
    shortDesc: 'HMM 프로젝트 단계별 일정을 정리합니다.',
    fileName: 'hmm_schedule.html',
    fileSize: '22KB',
    bot: '기본 채팅',
    chatDate: '10.19(토) 16:00',
    category: '영업지원',
    kind: 'HTML 문서',
    howto: '프로젝트 시작일을 입력하면 단계별 일정이 자동으로 계산됩니다.',
    scope: '',
    status: 'draft',
  },
};

const STATUS_META: Record<SubmissionStatus, { label: string; bg: string; fg: string }> = {
  review: { label: '검토 중',   bg: '#FDF1DC', fg: '#8A4B00' },
  change: { label: '수정 요청', bg: '#E8F0FC', fg: '#1D4F9C' },
  live:   { label: '게시됨',    bg: '#E6F4EC', fg: '#1E6B44' },
  reject: { label: '반려',      bg: '#FDECEA', fg: '#A8231A' },
  draft:  { label: '임시 저장', bg: '#EEF0F5', fg: '#3D4152' },
};

const SCOPE_OPTIONS = [
  { id: 'tenant', label: '에티버스 테넌트 전체', hint: '내 테넌트 안 모든 사용자' },
  { id: 'org',    label: '특정 고객사',          hint: '선택한 고객사 사용자만' },
  { id: 'people', label: '지정 사용자',          hint: '이름·이메일로 직접 선택' },
  { id: 'me',     label: '나만 보기',            hint: '관리자 승인 없이 저장' },
];

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export function AppSubmissionPage() {
  const { appId } = useParams<{ appId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const sub = appId ? SUBMISSIONS[appId] : null;

  // 편집 가능 여부
  const isEditable = sub?.status === 'draft' || sub?.status === 'change';
  const [appName,  setAppName]  = useState(sub?.appName  ?? '');
  const [shortDesc,setShortDesc]= useState(sub?.shortDesc ?? '');
  const [howto,    setHowto]    = useState(sub?.howto    ?? '');
  const [scope,    setScope]    = useState(sub?.scope    ?? '에티버스 테넌트 전체');

  if (!sub) {
    return (
      <>
        <Topbar title="앱 공유 신청" />
        <div className="page-content">
          <div className="page-content-inner" style={{ textAlign: 'center', paddingTop: 80 }}>
            <p>신청 정보를 찾을 수 없습니다.</p>
            <Button onClick={() => navigate('/library/my')} style={{ marginTop: 16 }}>← 내가 공유한 항목으로</Button>
          </div>
        </div>
      </>
    );
  }

  const sm = STATUS_META[sub.status];
  const [previewOpen, setPreviewOpen] = useState(false);

  const handleSubmit = () => {
    showToast('success', '신청이 제출됐습니다. 검토 후 결과를 알려드립니다.');
    navigate('/library/my');
  };

  const handleSaveDraft = () => {
    showToast('success', '임시 저장됐습니다.');
  };

  // 진행 단계 표시
  const steps = [
    { n: 1, label: '산출물 선택', done: true },
    { n: 2, label: '앱 정보',     done: sub.status !== 'draft', current: sub.status === 'draft' || sub.status === 'change' },
    { n: 3, label: '공개 범위·권한', done: sub.status !== 'draft' && sub.status !== 'change', current: false },
    { n: 4, label: '검토 요청',   done: sub.status === 'live' || sub.status === 'reject', current: false },
  ];

  return (
    <>
      {/* ── 산출물 HTML 미리보기 오버레이 ── */}
      {previewOpen && (
        <div className="asub-preview-overlay" role="dialog" aria-modal="true" aria-label="산출물 미리보기">
          <div className="asub-preview-topbar">
            <div className="asub-preview-topbar-left">
              <span className="asub-preview-file-badge">HTML</span>
              <strong>{sub.fileName}</strong>
              <span className="asub-preview-ver">{sub.fileSize} · {sub.chatDate}</span>
            </div>
            <button
              type="button"
              className="asub-preview-close"
              onClick={() => setPreviewOpen(false)}
              aria-label="닫기"
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
          <div className="asub-preview-body">
            <PreviewContent submission={sub} />
          </div>
          <div className="asub-preview-footer">
            목업 데이터로 표시됩니다. 실제 산출물은 챗봇 대화 내역에서 확인하세요.
          </div>
        </div>
      )}
      <Topbar
        title="라이브러리 공유 신청"
        leading={
          <button type="button" className="asub-back-btn" onClick={() => navigate('/library/my')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
          </button>
        }
      />

      <div className="page-content">
        <div className="page-content-inner asub-container">

          {/* 브레드크럼 */}
          <nav className="asub-breadcrumb">
            <Link to="/library">라이브러리</Link>
            <span>/</span>
            <Link to="/library/my">내가 공유한 항목</Link>
            <span>/</span>
            <span>공유 신청</span>
          </nav>

          {/* 상태 배지 */}
          <div className="asub-status-row">
            <h1 className="asub-title">{sub.appName}</h1>
            <span className="asub-status-badge" style={{ background: sm.bg, color: sm.fg }}>{sm.label}</span>
          </div>

          {/* 수정 요청 코멘트 */}
          {sub.comment && (
            <div className="asub-comment-box">
              <strong>수정 요청 사항</strong>
              <div className="asub-comment-inner">
                <span className="asub-comment-avatar">{sub.reviewer?.charAt(0)}</span>
                <div>
                  <p className="asub-comment-reviewer">{sub.reviewer}</p>
                  <p className="asub-comment-text">{sub.comment}</p>
                </div>
              </div>
            </div>
          )}

          {/* 단계 표시 */}
          <ol className="asub-steps">
            {steps.map((s) => (
              <li
                key={s.n}
                className={[
                  'asub-step',
                  s.done    ? 'asub-step--done'    : '',
                  s.current ? 'asub-step--current' : '',
                ].join(' ')}
              >
                {s.done && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                {s.n} {s.label}
              </li>
            ))}
          </ol>

          <div className="asub-body">
            <div className="asub-main">

              {/* 산출물 정보 */}
              <section className="asub-card">
                <h2 className="asub-card-title">산출물</h2>
                <div className="asub-artifact">
                  <span className="asub-artifact-icon">HTML</span>
                  <div className="asub-artifact-info">
                    <strong>{sub.fileName}</strong>
                    <span>{sub.bot} 챗봇 · {sub.chatDate} 대화 · {sub.fileSize}</span>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <button
                      type="button"
                      className="asub-preview-btn"
                      onClick={() => setPreviewOpen(true)}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                      미리보기
                    </button>
                    {isEditable && (
                      <button type="button" className="asub-artifact-change" disabled>다른 산출물 선택</button>
                    )}
                  </div>
                </div>
              </section>

              {/* 앱 정보 */}
              <section className="asub-card">
                <h2 className="asub-card-title">앱 정보</h2>
                <div className="asub-form">
                  <label className="asub-label">
                    앱 이름
                    <input
                      type="text"
                      className="asub-input"
                      value={appName}
                      onChange={(e) => isEditable && setAppName(e.target.value)}
                      readOnly={!isEditable}
                    />
                  </label>
                  <label className="asub-label">
                    한 줄 설명
                    <input
                      type="text"
                      className="asub-input"
                      value={shortDesc}
                      onChange={(e) => isEditable && setShortDesc(e.target.value)}
                      readOnly={!isEditable}
                    />
                  </label>
                  <div className="asub-form-row">
                    <label className="asub-label">
                      카테고리
                      <select className="asub-select" disabled={!isEditable} defaultValue={sub.category}>
                        {['데이터·리포트','클라우드','영업지원','기술지원','업무생산성','개발'].map(c => (
                          <option key={c}>{c}</option>
                        ))}
                      </select>
                    </label>
                    <fieldset className="asub-fieldset">
                      <legend>유형</legend>
                      <div className="asub-radio-group">
                        {(['HTML 문서', '웹앱'] as const).map((k) => (
                          <label key={k} className={`asub-radio-label${sub.kind === k ? ' asub-radio-label--checked' : ''}`}>
                            <input type="radio" name="kind" defaultChecked={sub.kind === k} disabled={!isEditable} />
                            {k}
                          </label>
                        ))}
                      </div>
                    </fieldset>
                  </div>
                  <label className="asub-label">
                    사용 방법
                    <textarea
                      className="asub-textarea"
                      rows={3}
                      value={howto}
                      onChange={(e) => isEditable && setHowto(e.target.value)}
                      readOnly={!isEditable}
                    />
                  </label>
                </div>
              </section>

              {/* 공개 범위 */}
              <section className="asub-card">
                <h2 className="asub-card-title">공개 범위 · 권한</h2>
                <div className="asub-notice">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                    <rect x="5" y="11" width="14" height="10" rx="2"/><path d="M8 11V7a4 4 0 018 0v4"/>
                  </svg>
                  <span>앱은 <strong>내 테넌트(에티버스) 안에서만</strong> 공유할 수 있어요.</span>
                </div>
                <div className="asub-scope-grid">
                  {SCOPE_OPTIONS.map((opt) => {
                    const checked = scope === opt.label || (opt.id === 'tenant' && scope === '에티버스 테넌트 전체');
                    return (
                      <label
                        key={opt.id}
                        className={`asub-scope-option${checked ? ' asub-scope-option--checked' : ''}`}
                      >
                        <input
                          type="radio"
                          name="scope"
                          checked={checked}
                          onChange={() => isEditable && setScope(opt.label)}
                          disabled={!isEditable}
                        />
                        <span>
                          <strong>{opt.label}</strong>
                          <span>{opt.hint}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </section>

            </div>

            {/* 사이드바 */}
            <aside className="asub-aside">
              <section className="asub-aside-panel">
                {isEditable ? (
                  <>
                    <Button className="asub-submit-btn" onClick={handleSubmit}>
                      검토 요청하기
                    </Button>
                    <button type="button" className="asub-draft-btn" onClick={handleSaveDraft}>
                      임시 저장
                    </button>
                  </>
                ) : (
                  <div className="asub-readonly-notice">
                    <span style={{ background: sm.bg, color: sm.fg }} className="asub-status-badge">
                      {sm.label}
                    </span>
                    <p>
                      {sub.status === 'review' && '검토 중입니다. 결과를 기다려주세요.'}
                      {sub.status === 'live'   && '앱이 게시되어 사용 중입니다.'}
                      {sub.status === 'reject' && '반려됐습니다. 공개 범위를 바꿔 다시 신청할 수 있어요.'}
                    </p>
                    {sub.status === 'reject' && (
                      <Button size="sm" onClick={() => showToast('info', '공개 범위를 변경한 후 재신청하세요.')}>
                        공개 범위 바꾸기
                      </Button>
                    )}
                  </div>
                )}
              </section>

              <section className="asub-aside-panel asub-info-panel">
                <h3 className="asub-aside-heading">신청 정보</h3>
                <dl className="asub-info-list">
                  <div><dt>파일</dt><dd>{sub.fileName}</dd></div>
                  <div><dt>원본 챗봇</dt><dd>{sub.bot}</dd></div>
                  <div><dt>생성 일시</dt><dd>{sub.chatDate}</dd></div>
                  <div><dt>카테고리</dt><dd>{sub.category}</dd></div>
                  <div><dt>유형</dt><dd>{sub.kind}</dd></div>
                </dl>
              </section>
            </aside>
          </div>

        </div>
      </div>
    </>
  );
}

export default AppSubmissionPage;
